// 計算フォーム管理フック

import { useState, useCallback, useMemo } from 'react';
import { FormData, CalculationInput, DEFAULT_VALUES, ApiResponse } from '../types';
import { validateForm, validateLogic } from '../utils/validation';
import { calculatePieces } from '../utils/api';

interface TouchedFields {
  [key: string]: boolean;
}

export function useCalculationForm() {
  const [formData, setFormData] = useState<FormData>({
    materialName: '',
    veneerWidth1: DEFAULT_VALUES.veneerWidth1.toString(),
    veneerHeight1: DEFAULT_VALUES.veneerHeight1.toString(),
    veneerWidth2: DEFAULT_VALUES.veneerWidth2.toString(),
    veneerHeight2: DEFAULT_VALUES.veneerHeight2.toString(),
    pieceWidth: '',
    pieceHeight: '',
    kerf: DEFAULT_VALUES.kerf.toString(),
    allowRotation1: true,
    allowRotation2: true,
  });

  const [touched, setTouched] = useState<TouchedFields>({});

  // バリデーション
  const errors = useMemo(() => {
    const basicErrors = validateForm(formData);
    if (Object.keys(basicErrors).length === 0) {
      return validateLogic(formData);
    }
    return basicErrors;
  }, [formData]);

  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0 && formData.materialName !== '';
  }, [errors, formData.materialName]);

  // フィールド変更ハンドラー
  const handleFieldChange = useCallback((
    fieldName: keyof FormData,
    value: string | boolean
  ) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    setTouched(prev => ({
      ...prev,
      [fieldName]: true,
    }));
  }, []);

  // フィールドのblurハンドラー
  const handleFieldBlur = useCallback((fieldName: keyof FormData) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true,
    }));
  }, []);

  // フォームリセット
  const resetForm = useCallback(() => {
    setFormData({
      materialName: '',
      veneerWidth1: DEFAULT_VALUES.veneerWidth1.toString(),
      veneerHeight1: DEFAULT_VALUES.veneerHeight1.toString(),
      veneerWidth2: DEFAULT_VALUES.veneerWidth2.toString(),
      veneerHeight2: DEFAULT_VALUES.veneerHeight2.toString(),
      pieceWidth: '',
      pieceHeight: '',
      kerf: DEFAULT_VALUES.kerf.toString(),
      allowRotation1: true,
      allowRotation2: true,
    });
    setTouched({});
  }, []);

  // フォーム送信ハンドラー
  const handleSubmit = useCallback(async (data: FormData): Promise<ApiResponse> => {
    // 最終的なバリデーション
    const submitErrors = validateLogic(data);
    if (Object.keys(submitErrors).length > 0) {
      // Update touched state to show all errors
      setTouched(prev => ({
        ...prev,
        ...Object.keys(submitErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      }));
      throw new Error('フォームに入力エラーがあります。各項目を確認してください。');
    }
    // …the rest of handleSubmit…

    // FormDataからCalculationInputに変換（新しい形式）
    const input: CalculationInput = {
      materialName: data.materialName,
      veneerConfigs: [
        {
          width: Number(data.veneerWidth1),
          height: Number(data.veneerHeight1),
          noRotation: !data.allowRotation1,
        },
        {
          width: Number(data.veneerWidth2),
          height: Number(data.veneerHeight2),
          noRotation: !data.allowRotation2,
        },
      ],
      pieceWidth: Number(data.pieceWidth),
      pieceHeight: Number(data.pieceHeight),
      kerf: Number(data.kerf),
    };

    // API呼び出し
    return await calculatePieces(input);
  }, []);

  // 特定フィールドのエラーを取得
  const getFieldError = useCallback((fieldName: keyof FormData) => {
    return errors[fieldName];
  }, [errors]);

  // 特定フィールドがタッチ済みかチェック
  const isFieldTouched = useCallback((fieldName: keyof FormData) => {
    return touched[fieldName] || false;
  }, [touched]);

  // 特定フィールドのエラー表示判定
  const shouldShowFieldError = useCallback((fieldName: keyof FormData) => {
    return !!(errors[fieldName] && touched[fieldName]);
  }, [errors, touched]);

  return {
    formData,
    errors,
    touched,
    isValid,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit,
    resetForm,
    getFieldError,
    isFieldTouched,
    shouldShowFieldError,
  };
}